import * as React from "react"
import type { Table as TanstackTable, Row } from "@tanstack/react-table"
import { flexRender as render } from "@tanstack/react-table"

import { cn } from "../../lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"

/* ------------------------------------------------------------------ */
/*  DataTable — wraps a TanStack table instance into the Eriko style  */
/* ------------------------------------------------------------------ */

interface DataTableProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
  /** TanStack React Table instance */
  table: TanstackTable<TData>
  /** Render a toolbar above the table (filters, search, actions) */
  toolbar?: React.ReactNode
  /** Render pagination below the table */
  pagination?: React.ReactNode
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void
  /** Render an expandable sub-component below a row (used with getExpandedRowModel) */
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactNode
  /** Message when no results */
  emptyMessage?: string
}

function DataTable<TData>({
  table,
  toolbar,
  pagination,
  onRowClick,
  renderSubComponent,
  emptyMessage = "No results.",
  className,
  ...props
}: DataTableProps<TData>) {
  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {toolbar && (
        <div className="flex items-center gap-1.5 px-6 pb-2">{toolbar}</div>
      )}

      <div className="rounded-lg border border-surface-300 bg-surface-0 overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-transparent cursor-default border-surface-300"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.column.columnDef.size != null ? header.getSize() : undefined }}
                  >
                    {header.isPlaceholder
                      ? null
                      : render(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() ? "selected" : undefined}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(!onRowClick && !renderSubComponent && "cursor-default")}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {render(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderSubComponent && row.getIsExpanded() && (
                    <TableRow className="hover:bg-transparent cursor-default">
                      <TableCell colSpan={row.getVisibleCells().length} className="p-0">
                        {renderSubComponent({ row })}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow className="hover:bg-transparent cursor-default">
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center text-surface-400"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between px-6 pt-2.5 pb-4">
          {pagination}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  DataTablePagination                                               */
/* ------------------------------------------------------------------ */

interface DataTablePaginationProps {
  /** e.g. "Showing 1-25 of 214" */
  label?: string
  children?: React.ReactNode
}

function DataTablePagination({ label, children }: DataTablePaginationProps) {
  return (
    <>
      {label && (
        <span className="text-[11px] text-surface-500">{label}</span>
      )}
      <div className="flex gap-1">{children}</div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  DataTablePageButton                                               */
/* ------------------------------------------------------------------ */

interface DataTablePageButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
}

function DataTablePageButton({
  active,
  className,
  children,
  ...props
}: DataTablePageButtonProps) {
  return (
    <button
      className={cn(
        "w-7 h-7 rounded-md border text-[11px] font-medium font-mono flex items-center justify-center transition-all duration-100 cursor-pointer",
        active
          ? "bg-surface-900 border-surface-900 text-white"
          : "border-surface-300 bg-surface-0 text-surface-600 hover:border-surface-500 hover:text-surface-900",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export { DataTable, DataTablePagination, DataTablePageButton }
